import React, { useCallback, useLayoutEffect, useRef } from 'react'
import type { SxProps, Theme } from '@mui/material/styles'
import { StickyTableHeader } from 'vh-sticky-table-header'
import { Typography } from '@mui/material'
import useWindowDimensions from 'src/hook/useWindowDimensions'

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
    if (!ref) {
        return
    }
    if (typeof ref === 'function') {
        ref(value)
        return
    }
    ;(ref as React.MutableRefObject<T | null>).current = value
}

interface TableWithStickyHeaderProps {
    children: React.ReactNode
    tableRef?: React.Ref<HTMLTableElement>
    scrollContainerRef?: React.Ref<HTMLDivElement>
    scrollContainerSx?: SxProps<Theme>
    scrollContainerClassName?: string
    onScrollContainerMouseDown?: React.MouseEventHandler<HTMLDivElement>
    tableStyle?: React.CSSProperties
}

const TableWithStickyHeader = ({
    children,
    tableRef,
    scrollContainerRef,
    scrollContainerSx,
    scrollContainerClassName,
    onScrollContainerMouseDown,
    tableStyle,
}: TableWithStickyHeaderProps) => {
    const internalTableRef = useRef<HTMLTableElement | null>(null)
    const tableCloneRef = useRef<HTMLTableElement>(null)
    const { width } = useWindowDimensions()

    const setTableRef = useCallback(
        (node: HTMLTableElement | null) => {
            internalTableRef.current = node
            assignRef(tableRef, node)
        },
        [tableRef],
    )

    useLayoutEffect(() => {
        if (internalTableRef.current && tableCloneRef.current) {
            const sticky = new StickyTableHeader(
                internalTableRef.current,
                tableCloneRef.current,
                { max: 74 },
            )
            return () => sticky.destroy()
        }
    }, [children, width])

    return (
        <Typography component={'div'}>
            <Typography
                component={'div'}
                className={scrollContainerClassName ?? 'table_container'}
                ref={scrollContainerRef}
                onMouseDown={onScrollContainerMouseDown}
                sx={[
                    { overflowX: 'auto', overflowY: 'hidden' },
                    ...(Array.isArray(scrollContainerSx)
                        ? scrollContainerSx
                        : scrollContainerSx
                          ? [scrollContainerSx]
                          : []),
                ]}
            >
                <table
                    style={{ overflowX: 'scroll', marginBottom: 0, ...tableStyle }}
                    ref={setTableRef}
                >
                    {children}
                </table>
            </Typography>
            <Typography
                component={'div'}
                className="clone_table table_container"
                sx={{
                    top: {
                        xs: '56px !important',
                        sm: '64px !important',
                        md: '74px !important',
                    },
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    marginBottom: 0,
                    '& thead': {
                        backgroundColor: '#fff',
                        zIndex: 1000,
                    },
                }}
            >
                <table ref={tableCloneRef} />
            </Typography>
        </Typography>
    )
}

export default TableWithStickyHeader
